import React, { useState, useMemo, useEffect } from "react";
import "./OpportunityTable.css";

const OpportunityTable = ({ opportunities, onEdit }) => {
  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem("opportunityTableSort");
    return saved ? JSON.parse(saved) : { key: "status", direction: "asc" };
  });

  useEffect(() => {
    localStorage.setItem("opportunityTableSort", JSON.stringify(sortConfig));
  }, [sortConfig]);

  const getCoverageTypeDisplay = (opp) => {
    return (
      opp.coverageTypeName ||
      opp.coverage_type_name ||
      opp.coverage_type ||
      "-"
    );
  };

  const getMedicareSupplementPlanType = (opp) => {
    return (
      opp.coverageTypePlanTypeName ||
      opp.coverage_type_plan_type_name ||
      opp.coverage_type_plan_type ||
      opp.planTypeName ||
      opp.plan_type_name ||
      opp.planType ||
      opp.plan_type ||
      opp.medicare_supplement_plan_type ||
      ""
    );
  };

  const getPlanDisplay = (opp) => {
    const coverageType = getCoverageTypeDisplay(opp);

    if (String(coverageType).trim().toLowerCase() === "medicare supplement") {
      return (
        getMedicareSupplementPlanType(opp) ||
        opp.productName ||
        opp.product_name ||
        opp.plan_name ||
        opp.planName ||
        "-"
      );
    }

    return (
      opp.productName ||
      opp.product_name ||
      opp.plan_name ||
      opp.planName ||
      opp.plan_type ||
      opp.planType ||
      "-"
    );
  };

  const getSortValue = (opp, key) => {
    switch (key) {
      case "status":
        return opp.status || "";
      case "coverageTypeName":
        return getCoverageTypeDisplay(opp);
      case "carrierName":
        return opp.carrierName || opp.carrier_name || "";
      case "plan_display":
        return getPlanDisplay(opp);
      case "next_step":
        return opp.next_step || opp.nextStep || "";
      default:
        return opp[key] || "";
    }
  };

  const requestSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };

  const sortedOpps = useMemo(() => {
    const sortable = [...opportunities];

    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aVal = getSortValue(a, sortConfig.key).toString().toLowerCase();
        const bVal = getSortValue(b, sortConfig.key).toString().toLowerCase();

        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return sortable;
  }, [opportunities, sortConfig]);

  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  return (
    <table className="sales-table opportunity-sales-table">
      <thead>
        <tr>
          <th onClick={() => requestSort("status")}>
            Status {getSortArrow("status")}
          </th>
          <th onClick={() => requestSort("coverageTypeName")}>
            Coverage Type {getSortArrow("coverageTypeName")}
          </th>
          <th onClick={() => requestSort("carrierName")}>
            Carrier {getSortArrow("carrierName")}
          </th>
          <th onClick={() => requestSort("plan_display")}>
            Plan {getSortArrow("plan_display")}
          </th>
          <th onClick={() => requestSort("next_step")}>
            Next Step {getSortArrow("next_step")}
          </th>
        </tr>
      </thead>

      <tbody>
        {sortedOpps.length === 0 ? (
          <tr>
            <td colSpan="5" className="sales-empty-cell">
              No opportunities found.
            </td>
          </tr>
        ) : (
          sortedOpps.map((opp) => (
            <tr
              key={`opp-${opp.id}-${getCoverageTypeDisplay(opp)}`}
              onClick={() => onEdit(opp)}
              className="sales-click-row opportunity-click-row"
            >
              <td>
                <span className="opportunity-status-text">{opp.status || "-"}</span>
              </td>

              <td>
                <div className="sales-primary-cell">
                  <div className="sales-name">
                    {getCoverageTypeDisplay(opp)}
                  </div>
                </div>
              </td>

              <td>
                <div className="sales-primary-cell">
                  <div className="sales-name">
                    {opp.carrierName || opp.carrier_name || "-"}
                  </div>
                </div>
              </td>

              <td>
                <div className="sales-primary-cell">
                  <div className="sales-name">{getPlanDisplay(opp)}</div>
                </div>
              </td>

              <td>
                <div className="sales-primary-cell">
                  <div className="sales-name">
                    {opp.next_step || opp.nextStep || "-"}
                  </div>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

export default OpportunityTable;